import React from "react";
import { Box, Typography, Grid, Stack, IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import { ShieldCheck, Cloud, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";

// Lucide no longer ships brand/social icons, so these are small inline SVGs
// kept minimal and styled via currentColor to match the IconButton color.
const LinkedinIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3V9zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.6c0-1.34-.02-3.06-1.87-3.06-1.87 0-2.16 1.46-2.16 2.96V21h-4V9z" />
  </svg>
);
const InstagramIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.97.24 2.43.4a4.9 4.9 0 0 1 1.77 1.15 4.9 4.9 0 0 1 1.15 1.77c.16.46.35 1.26.4 2.43.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.24 1.97-.4 2.43a4.9 4.9 0 0 1-1.15 1.77 4.9 4.9 0 0 1-1.77 1.15c-.46.16-1.26.35-2.43.4-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.97-.24-2.43-.4a4.9 4.9 0 0 1-1.77-1.15 4.9 4.9 0 0 1-1.15-1.77c-.16-.46-.35-1.26-.4-2.43C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.24-1.97.4-2.43A4.9 4.9 0 0 1 3.82 3c.53-.5 1.13-.9 1.77-1.15.46-.16 1.26-.35 2.43-.4C9.29 2.21 9.67 2.2 12 2.2zm0 1.8c-3.15 0-3.5.01-4.73.07-.96.04-1.48.2-1.82.34-.46.18-.79.4-1.13.74-.34.34-.56.67-.74 1.13-.13.34-.3.86-.34 1.82C3.18 8.5 3.17 8.85 3.17 12s.01 3.5.07 4.73c.04.96.2 1.48.34 1.82.18.46.4.79.74 1.13.34.34.67.56 1.13.74.34.13.86.3 1.82.34 1.23.06 1.58.07 4.73.07s3.5-.01 4.73-.07c.96-.04 1.48-.2 1.82-.34.46-.18.79-.4 1.13-.74.34-.34.56-.67.74-1.13.13-.34.3-.86.34-1.82.06-1.23.07-1.58.07-4.73s-.01-3.5-.07-4.73c-.04-.96-.2-1.48-.34-1.82a3.06 3.06 0 0 0-.74-1.13 3.06 3.06 0 0 0-1.13-.74c-.34-.13-.86-.3-1.82-.34-1.23-.06-1.58-.07-4.73-.07zm0 4.03a3.97 3.97 0 1 1 0 7.94 3.97 3.97 0 0 1 0-7.94zm0 1.8a2.17 2.17 0 1 0 0 4.34 2.17 2.17 0 0 0 0-4.34zm5.06-1.98a.93.93 0 1 1-1.86 0 .93.93 0 0 1 1.86 0z" />
  </svg>
);
const YoutubeIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M23 12s0-3.36-.43-4.96a2.78 2.78 0 0 0-1.96-1.97C18.96 4.6 12 4.6 12 4.6s-6.96 0-8.61.47A2.78 2.78 0 0 0 1.43 7.04C1 8.64 1 12 1 12s0 3.36.43 4.96a2.78 2.78 0 0 0 1.96 1.97c1.65.47 8.61.47 8.61.47s6.96 0 8.61-.47a2.78 2.78 0 0 0 1.96-1.97C23 15.36 23 12 23 12zM9.75 15.4V8.6L15.7 12l-5.95 3.4z" />
  </svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M13.5 21v-7.6h2.55l.38-2.96h-2.93V8.55c0-.86.24-1.44 1.47-1.44h1.57V4.46c-.27-.04-1.2-.12-2.28-.12-2.26 0-3.8 1.38-3.8 3.91v2.18H7.99v2.96h2.47V21h3.04z" />
  </svg>
);

// Default footer link groups. The "Company" group now points to real routes
// (About Us / Privacy Policy) instead of placeholder anchors.
const defaultGroups = [
  ["Learning", "AWS", "Azure", "Cyber Security", "DevOps"],
  ["Services", "Cloud Consulting", "Managed Services", "Software Development", "Digital Transformation"],
  ["Corporate", "Training", "Client Portal", "Reports", "Billing"],
  ["Talent", "Recruitment", "Screening", "Hiring Dashboard", "Placement"],
  ["Resources", "Blogs", "Whitepapers", "Events", "Guides"],
];

const badges = [
  { label: "ISO 9001", icon: ShieldCheck },
  { label: "AWS Partner", icon: Cloud },
  { label: "Microsoft", icon: ShieldCheck },
  { label: "Cisco", icon: ShieldCheck },
];

// Fallback social links (used whenever the corresponding settings.* field
// isn't configured yet, so the footer always has working links out of the box).
const DEFAULT_SOCIALS = {
  facebook: "https://www.facebook.com/pages/Netlabs-ITS/196932360429005",
  linkedin: "https://www.linkedin.com/company/netlabs-its",
  instagram: "https://www.instagram.com/netlabsits/",
  youtube: "https://www.youtube.com/@netlabits5515/videos",
};

// Brand-accurate gradients + glow colors for the "colorful & shiny" hover state.
const SOCIAL_BRANDING = {
  Facebook: {
    gradient: "linear-gradient(135deg, #1877F2 0%, #0C5DD1 100%)",
    glow: "rgba(24,119,242,0.45)",
  },
  LinkedIn: {
    gradient: "linear-gradient(135deg, #0A66C2 0%, #004182 100%)",
    glow: "rgba(10,102,194,0.45)",
  },
  Instagram: {
    gradient:
      "linear-gradient(135deg, #FEDA75 0%, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5 100%)",
    glow: "rgba(214,41,118,0.45)",
  },
  YouTube: {
    gradient: "linear-gradient(135deg, #FF0000 0%, #CC0000 100%)",
    glow: "rgba(255,0,0,0.4)",
  },
};

const DEFAULT_MAP_EMBED =
  "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7007.911796388041!2d77.222092!3d28.571087!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce25ebe723353%3A0xe468b1282e8ec182!2sNetlabsIT%20Consultancy%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1785687918945!5m2!1sen!2sin";

const legalLinks = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms of Service", to: "/terms-of-service" },
  { label: "Sitemap", to: "/sitemap" },
];

export default function Footer() {
  const settings = useSettings();

  // Dynamic values, all with sensible fallbacks so the footer never breaks
  // if a given setting hasn't been configured in the admin panel yet.
  const logoUrl = settings.logo_url || "/logo/logo.png";
  const companyBlurb =
    settings.footer_description ||
    "A premium technology ecosystem across professional training, corporate services, cloud, managed IT, software, workforce upskilling, and enterprise solutions.";
  const address = settings.company_address || "New Delhi, India";
  const phone = settings.company_phone || "";
  const email = settings.company_email || "";
  const mapEmbedUrl = settings.map_embed_url || DEFAULT_MAP_EMBED;

  const socials = [
    { icon: FacebookIcon, href: settings.social_facebook || DEFAULT_SOCIALS.facebook, label: "Facebook" },
    { icon: LinkedinIcon, href: settings.social_linkedin || DEFAULT_SOCIALS.linkedin, label: "LinkedIn" },
    { icon: InstagramIcon, href: settings.social_instagram || DEFAULT_SOCIALS.instagram, label: "Instagram" },
    { icon: YoutubeIcon, href: settings.social_youtube || DEFAULT_SOCIALS.youtube, label: "YouTube" },
  ].filter((s) => Boolean(s.href));

  // Allow the "Company" group to be overridden via settings, otherwise fall
  // back to real internal routes.
  const groups = settings.footer_groups || [
    ...defaultGroups,
    ["Company", "About Us", "Contact", "Privacy Policy", "Terms"],
  ];

  const linkTo = (group, link) => {
    if (link === "About Us") return "/about-us";
    if (link === "Privacy Policy") return "/privacy-policy";
    if (link === "Terms") return "/terms-of-service";
    if (link === "Contact") return "/#contact";
    return "#";
  };

  return (
    <Box component="footer" sx={{ position: "relative", bgcolor: "#F8FAFC", pt: 10, pb: 6, overflow: "hidden" }}>
      {/* top accent line */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(90deg, transparent, rgba(37,99,235,0.35), transparent)",
        }}
      />

      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 } }}>
        <Grid container spacing={{ xs: 5, lg: 6 }}>
          <Grid item xs={12} lg={4}>
            <Box component="img" src={logoUrl} alt={settings.site_name || "NetLabs ITS"} sx={{ height: 48, width: "auto", mb: 2.5 }} />
            <Typography sx={{ maxWidth: 360, lineHeight: 1.8, fontSize: 14, color: "text.secondary", mb: 3 }}>
              {companyBlurb}
            </Typography>

            {(address || phone || email) && (
              <Stack spacing={1} sx={{ mb: 3 }}>
                {address && (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <MapPin size={15} color="#2563EB" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{address}</Typography>
                  </Stack>
                )}
                {phone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Phone size={15} color="#2563EB" style={{ flexShrink: 0 }} />
                    <Typography
                      component="a"
                      href={`tel:${phone}`}
                      sx={{ fontSize: 13, color: "text.secondary", textDecoration: "none", "&:hover": { color: "#2563EB" } }}
                    >
                      {phone}
                    </Typography>
                  </Stack>
                )}
                {email && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Mail size={15} color="#2563EB" style={{ flexShrink: 0 }} />
                    <Typography
                      component="a"
                      href={`mailto:${email}`}
                      sx={{ fontSize: 13, color: "text.secondary", textDecoration: "none", "&:hover": { color: "#2563EB" } }}
                    >
                      {email}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3, rowGap: 1 }}>
              {badges.map(({ label, icon: Icon }) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    border: "1px solid rgba(15,23,42,0.1)",
                    bgcolor: "white",
                    borderRadius: "999px",
                    px: 1.6,
                    py: 0.55,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  <Icon size={13} color="#2563EB" strokeWidth={2.3} />
                  {label}
                </Box>
              ))}
            </Stack>

            {/* Social icons — brand-colored, glossy hover state */}
            <Stack direction="row" spacing={1.25}>
              {socials.map(({ icon: Icon, href, label }) => {
                const brand = SOCIAL_BRANDING[label] || {
                  gradient: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                  glow: "rgba(37,99,235,0.45)",
                };
                return (
                  <IconButton
                    key={label}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    size="small"
                    className="social-icon-btn"
                    sx={{
                      position: "relative",
                      width: 38,
                      height: 38,
                      overflow: "hidden",
                      border: "1px solid rgba(255,255,255,0.35)",
                      background: brand.gradient,
                      backgroundSize: "160% 160%",
                      backgroundPosition: "0% 0%",
                      color: "white",
                      isolation: "isolate",
                      boxShadow: `0 6px 16px -4px ${brand.glow}, 0 2px 5px -1px ${brand.glow}, inset 0 1px 0 rgba(255,255,255,0.5)`,
                      transition:
                        "transform 0.3s ease, box-shadow 0.3s ease, background-position 0.5s ease",
                      // shine sweep across the glossy surface, always subtly present, intensifies on hover
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-60%",
                        width: "35%",
                        height: "100%",
                        background:
                          "linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)",
                        transform: "skewX(-20deg)",
                        opacity: 0.9,
                        zIndex: 2,
                        pointerEvents: "none",
                      },
                      "&:hover": {
                        transform: "translateY(-3px) scale(1.1)",
                        backgroundPosition: "100% 100%",
                        boxShadow: `0 12px 26px -6px ${brand.glow}, 0 4px 10px -2px ${brand.glow}, inset 0 1px 0 rgba(255,255,255,0.6)`,
                      },
                      "&:hover::after": {
                        left: "130%",
                        transition: "left 0.6s ease",
                      },
                      "&:active": { transform: "translateY(-1px) scale(1.03)" },
                    }}
                  >
                    <Icon style={{ position: "relative", zIndex: 3 }} />
                  </IconButton>
                );
              })}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Grid container spacing={3}>
              {groups.map(([heading, ...links]) => (
                <Grid item xs={6} sm={4} key={heading}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 13, letterSpacing: "0.02em", mb: 1.75, color: "#0F172A" }}
                  >
                    {heading}
                  </Typography>
                  <Stack spacing={1.1}>
                    {links.map((link) => {
                      const to = linkTo(heading, link);
                      const isInternal = to !== "#";
                      return (
                        <Box
                          key={link}
                          component={isInternal ? Link : "a"}
                          {...(isInternal ? { to } : { href: "#" })}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            width: "fit-content",
                            fontSize: 13.5,
                            color: "text.secondary",
                            textDecoration: "none",
                            transition: "color 0.2s ease, transform 0.2s ease",
                            "&:hover": { color: "#2563EB", transform: "translateX(2px)" },
                          }}
                        >
                          {link}
                        </Box>
                      );
                    })}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* Map */}
        {/* <Box
          sx={{
            mt: 7,
            borderRadius: "16px",
            overflow: "hidden",
            border: "1px solid rgba(15,23,42,0.08)",
          }}
        >
          <Box
            component="iframe"
            src={mapEmbedUrl}
            title="NetLabs IT Consultancy location"
            width="100%"
            height="320"
            sx={{ border: 0, display: "block" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </Box> */}

        {/* CTA strip */}
        <Box
          sx={{
            mt: 4,
            p: { xs: 2.5, md: 3 },
            borderRadius: "16px",
            bgcolor: "white",
            border: "1px solid rgba(15,23,42,0.08)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0F172A", mb: 0.4 }}>
              Looking to upskill your team or hire trained talent?
            </Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Talk to our experts and explore the right program for your business.
            </Typography>
          </Box>
          <Box
            component={Link}
            to="/business"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 13.5,
              fontWeight: 700,
              color: "#2563EB",
              textDecoration: "none",
              flexShrink: 0,
              "&:hover": { gap: 1.25 },
              transition: "gap 0.2s ease",
            }}
          >
            Talk To Experts <ArrowUpRight size={16} strokeWidth={2.3} />
          </Box>
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            mt: 5,
            pt: 3,
            borderTop: "1px solid rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
         <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 12.5 }}>
           © {new Date().getFullYear()} {settings.footer_text || "NetLabs ITs | All rights reserved."}
          </Typography>
          <Stack direction="row" spacing={2.5}>
            {legalLinks.map(({ label, to }) => (
              <Box
                key={label}
                component={Link}
                to={to}
                sx={{
                  fontSize: 12.5,
                  color: "text.secondary",
                  textDecoration: "none",
                  "&:hover": { color: "#2563EB" },
                }}
              >
                {label}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}